'use client';

import { useState, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SCalendar } from '../SCalendar';
import { SIcon, type SIconName } from '../SIcon';
import { type SColor } from '../../lib/color';
import { cn } from '../../lib/cn';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { type STooltipProps } from '../STooltip';

export type SDatePickerSize = SFieldSize;

export interface SDatePickerProps {
  /** 선택 날짜 (YYYY-MM-DD) */
  value?: string | null;
  /** 선택 변경 (sdUpdate) */
  onValueChange?: (date: string) => void;
  /** 보이는 연·월 변경 (sdViewChange) */
  onViewChange?: (view: { year: number; month: number }) => void;
  size?: SDatePickerSize;
  placeholder?: string;
  /** 선택 가능 범위 [시작, 종료] */
  selectable?: [string, string];
  disabled?: boolean;
  width?: number | string;
  name?: string;
  rules?: Rule[];
  status?: SFieldStatus;
  // ── SField ──
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  style?: CSSProperties;
}

/** SDatePicker — sd-date-picker 포팅. SField + SPopover(SCalendar). */
export function SDatePicker({
  value,
  onValueChange,
  onViewChange,
  size = 'sm',
  placeholder = 'YYYY-MM-DD',
  selectable,
  disabled = false,
  width,
  name,
  rules,
  status,
  label,
  labelWidth,
  icon,
  iconColor,
  labelTooltip,
  labelTooltipProps,
  addonLabel,
  addonAlign,
  hint,
  error,
  errorMessage,
  className,
  style,
}: SDatePickerProps) {
  const portalContainer = usePortalContainer();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const hasValue = value != null && value !== '';

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);
  // 트리거 치수 (sd-date-picker DATEPICKER_SIZE_MAP)
  const dims =
    size === 'md'
      ? { padX: 16, gap: 12, icon: 20, minW: 104, fontSize: 14, lineHeight: 24, radius: 6 }
      : { padX: 12, gap: 8, icon: 16, minW: 80, fontSize: 12, lineHeight: 20, radius: 4 };

  const select = (date: string) => {
    onValueChange?.(date);
    if (rules && rules.length > 0) setRuleError(runRules(date, rules));
    setOpen(false);
  };

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      size={size}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused || open}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      width={width}
      disabled={disabled}
      className={className}
      style={style}
    >
      <RPopover.Root open={open} onOpenChange={o => !disabled && setOpen(o)}>
        <RPopover.Trigger asChild>
          <button
            type="button"
            name={name}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className="flex h-full w-full items-center overflow-hidden bg-transparent outline-none disabled:cursor-not-allowed"
            style={{ padding: `0 ${dims.padX}px`, gap: dims.gap, borderRadius: dims.radius }}
          >
            {/* 원본: 아이콘(왼쪽, 고정) + 텍스트(flex:1, 중앙정렬, ellipsis) */}
            <SIcon
              name="date"
              size={dims.icon}
              color={
                disabled
                  ? 'var(--cmp-datepicker-icon-disabled)'
                  : 'var(--cmp-datepicker-icon-default)'
              }
              className="flex-shrink-0"
            />
            <span
              className="flex-1 truncate text-center"
              style={{
                minWidth: dims.minW,
                fontSize: dims.fontSize,
                lineHeight: `${dims.lineHeight}px`,
                color: disabled
                  ? 'var(--cmp-datepicker-text-disabled)'
                  : hasValue
                    ? 'var(--cmp-datepicker-text-default)'
                    : 'var(--cmp-field-hint-color)',
              }}
            >
              {hasValue ? value : placeholder}
            </span>
          </button>
        </RPopover.Trigger>
        <RPopover.Portal container={portalContainer}>
          <RPopover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            className={cn('z-50', FLOATING_SLIDE_ANIM)}
          >
            <SCalendar
              value={value ?? undefined}
              selectable={selectable}
              elevated
              onValueChange={select}
              onViewChange={onViewChange}
            />
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </SField>
  );
}
